import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadXP, getLevelInfo, XPState } from '../data/xp';
import { MODULE_ORDER, isUnlocked, getThisWeekIndex, daysUntilUnlock, recordStart } from '../data/progress';

const MODULES = [
  {
    to: '/quest',
    emoji: '⚔️',
    title: 'Privacy Quest',
    subtitle: 'Chapter-based adventure',
    description: 'Navigate real-world privacy decisions through an interactive story. Your choices have consequences — just like online.',
    tag: 'Story · Decision Game',
    tagColor: '#6366f1',
    accentColor: '#4f46e5',
    xpRange: '20–90 XP per scene',
    chapters: 3,
    difficulty: 'Beginner',
  },
  {
    to: '/algorithm',
    emoji: '🔍',
    title: 'Algorithm Mirror',
    subtitle: 'See what they see',
    description: 'Browse a simulated social feed. Watch your profile build in real-time as the algorithm infers who you are from your clicks.',
    tag: 'Interactive · Eye-opening',
    tagColor: '#10b981',
    accentColor: '#059669',
    xpRange: '100 XP on completion',
    chapters: 1,
    difficulty: 'Beginner',
  },
  {
    to: '/dataheist',
    emoji: '🕵️',
    title: 'Data Heist',
    subtitle: 'Spot the dark patterns',
    description: 'Play as the corporation OR the defender. Identify the sneaky tricks companies use to get your data — then block them.',
    tag: 'Puzzle · Timed',
    tagColor: '#f59e0b',
    accentColor: '#d97706',
    xpRange: '50–150 XP',
    chapters: 1,
    difficulty: 'Intermediate',
  },
  {
    to: '/phantom',
    emoji: '👻',
    title: 'Phantom Footprint',
    subtitle: "Trace a teen's invisible data trail",
    description: "Live through Maya's ordinary day and discover the invisible data silently collected at every step — then replay it with privacy mode on.",
    tag: 'Simulation · Investigative',
    tagColor: '#00f5d4',
    accentColor: '#00b894',
    xpRange: '120+ XP',
    chapters: 8,
    difficulty: 'Beginner',
  },
  {
    to: '/permissions',
    emoji: '',
    title: 'Permission Impossible',
    subtitle: 'You are the Chief Privacy Officer',
    description: 'Six apps request permissions on a fictional teen\'s phone. For each one, decide: Allow, Allow While Using, or Deny — then see exactly what the app gains and what the teen loses.',
    tag: 'Strategy · Role-Play',
    tagColor: '#6366f1',
    accentColor: '#4f46e5',
    xpRange: '200+ XP',
    chapters: 6,
    difficulty: 'Intermediate',
  },
  {
    to: '/trial',
    emoji: '',
    title: 'Terms & Conditions: The Trial',
    subtitle: 'Judge 15 real-world privacy clauses',
    description: 'Three trial days. Fifteen paraphrased clauses from real apps. Verdict: Fair, Suspicious, or Predatory. The Honourable J. Wright responds to every decision — correctly or otherwise.',
    tag: 'Courtroom · Analysis',
    tagColor: '#a78bfa',
    accentColor: '#7c3aed',
    xpRange: '350 XP',
    chapters: 3,
    difficulty: 'Advanced',
  },
  {
    to: '/leaklab',
    emoji: '',
    title: 'The Leak Lab',
    subtitle: 'Contain the breach before time runs out',
    description: 'Five data breaches. Five minutes each. Identify the right containment actions, stop the clock, then draft the legal disclosure notification. Every wrong move costs time.',
    tag: 'Escape Room · Incident Response',
    tagColor: '#ef4444',
    accentColor: '#dc2626',
    xpRange: '350 XP',
    chapters: 5,
    difficulty: 'Advanced',
  },
  {
    to: '/detective',
    emoji: '',
    title: 'Deepfake Detective',
    subtitle: 'Train your eye for manipulation',
    description: 'Analyse AI-generated media side-by-side with authentic originals. Identify the forensic tells that reveal synthetic audio, video, and images.',
    tag: 'Forensics · Analysis',
    tagColor: '#e11d48',
    accentColor: '#be123c',
    xpRange: '200+ XP',
    chapters: 5,
    difficulty: 'Advanced',
  },
];

const FACTS = [
  'The average website loads 70+ trackers before you\'ve read a single word.',
  'Your phone\'s accelerometer data alone can identify you — no GPS needed.',
  'Companies can predict your personality from 10 Facebook likes.',
  'Under PIPEDA, you can request & delete any data a company holds about you.',
  'Instagram can infer your mood from the time of day you post.',
  'Data brokers sell profiles on teenagers to political campaigns.',
];

export default function Home() {
  const [xp, setXp] = useState<XPState>(loadXP());
  const [factIndex, setFactIndex] = useState(0);
  const { current, next, progress } = getLevelInfo(xp.total);

  useEffect(() => {
    const loaded = loadXP();
    setXp(loaded);
    // Bootstrap startedAt if the user already has XP from a prior session
    if (loaded.completedModules.length > 0) recordStart();
    const interval = setInterval(() => {
      setFactIndex(i => (i + 1) % FACTS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const thisWeekIdx = getThisWeekIndex(xp.completedModules);
  const thisWeekMod = MODULES[thisWeekIdx];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-100)' }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 60%, #4f46e5 100%)',
        padding: '64px 24px 48px',
        textAlign: 'center',
        color: 'white',
      }}>

        <h1 style={{ fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 16, color: '#e0e7ff' }}>
          Your data is being collected.<br />
          <span style={{ color: '#818cf8' }}>Learn how to fight back.</span>
        </h1>
        <p style={{ fontSize: 18, color: '#c7d2fe', maxWidth: 580, margin: '0 auto 32px', lineHeight: 1.6 }}>
          Three interactive games that teach you privacy, algorithms, and digital rights — the way they should be taught.
        </p>

        {/* XP Dashboard */}
        <div style={{
          display: 'inline-flex',
          gap: 24,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: '20px 32px',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.12)',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Your Level</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#e0e7ff' }}>Lv. {xp.level}</div>
            <div style={{ fontSize: 13, color: '#a5b4fc' }}>{current.label}</div>
          </div>
          <div style={{ width: 1, height: 48, background: 'rgba(255,255,255,0.15)' }} />
          <div>
            <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Total XP</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#e0e7ff' }}>{xp.total}</div>
            {next && <div style={{ fontSize: 13, color: '#a5b4fc' }}>{next.min - xp.total} to Lv.{next.level}</div>}
          </div>
          <div>
            {/* XP bar */}
            <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Progress</div>
            <div style={{ width: 160, height: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #818cf8, #6366f1)', borderRadius: 5, transition: 'width 0.6s' }} />
            </div>
            <div style={{ fontSize: 12, color: '#a5b4fc', marginTop: 4 }}>{progress}% to next level</div>
          </div>
          <div style={{ width: 1, height: 48, background: 'rgba(255,255,255,0.15)' }} />
          <div>
            <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Identity</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e0e7ff' }}>{xp.alias}</div>
            <div style={{ fontSize: 12, color: '#a5b4fc' }}>{xp.completedModules.length} module{xp.completedModules.length !== 1 ? 's' : ''} done</div>
          </div>
        </div>
      </div>

      {/* Rotating fact banner */}
      <div style={{
        background: '#312e81',
        padding: '14px 24px',
        textAlign: 'center',
        fontSize: 14,
        color: '#c7d2fe',
        fontWeight: 500,
      }}>
        <span style={{ color: '#818cf8', fontWeight: 700, marginRight: 8 }}>⚡ Did you know?</span>
        <span style={{ transition: 'opacity 0.3s' }}>{FACTS[factIndex]}</span>
      </div>

      {/* This week's module */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
          This week's module
        </div>
        <Link to={thisWeekMod.to} style={{ textDecoration: 'none', display: 'block' }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 60%, #4f46e5 100%)',
            borderRadius: 20,
            padding: '28px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            boxShadow: '0 4px 24px rgba(79,70,229,0.25)',
            cursor: 'pointer',
            transition: 'transform 0.15s',
            flexWrap: 'wrap',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; }}
          >
            <span style={{ fontSize: 52 }}>{thisWeekMod.emoji || '🎯'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                {thisWeekMod.tag}
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: '#e0e7ff', margin: '0 0 4px' }}>{thisWeekMod.title}</h2>
              <p style={{ fontSize: 15, color: '#c7d2fe', margin: 0, lineHeight: 1.5 }}>{thisWeekMod.description}</p>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)', borderRadius: 12,
              padding: '12px 20px', textAlign: 'center', minWidth: 100,
            }}>
              <div style={{ fontSize: 13, color: '#a5b4fc', fontWeight: 600 }}>Earn up to</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#e0e7ff' }}>{thisWeekMod.xpRange}</div>
              <div style={{
                marginTop: 10, background: '#4f46e5', color: 'white',
                borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 700,
              }}>
                {xp.completedModules.some(m => m.startsWith(thisWeekMod.to.slice(1))) ? 'Play again' : 'Start'} →
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Module cards */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1e1b4b', marginBottom: 8 }}>All modules</h2>
        <p style={{ color: '#6b7280', marginBottom: 32, fontSize: 15 }}>Each module earns XP. Complete all eight to reach Privacy Champion.</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {MODULES.map((mod, modIdx) => {
            const done = xp.completedModules.some(m => m.startsWith(mod.to.slice(1)));
            const moduleKey = MODULE_ORDER[modIdx];
            const unlocked = moduleKey ? isUnlocked(modIdx, xp.completedModules) : true;
            const days = moduleKey ? daysUntilUnlock(modIdx, xp.completedModules) : 0;

            const card = (
              <div style={{
                background: unlocked ? 'white' : '#f9fafb',
                borderRadius: 20,
                padding: 28,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: `2px solid ${done ? mod.accentColor : unlocked ? 'transparent' : '#e5e7eb'}`,
                transition: 'transform 0.15s, box-shadow 0.15s',
                cursor: unlocked ? 'pointer' : 'not-allowed',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                opacity: unlocked ? 1 : 0.6,
              }}
                onMouseEnter={e => { if (unlocked) { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; } }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 40 }}>{mod.emoji || (unlocked ? '🎯' : '🔒')}</span>
                  {done && <span style={{ fontSize: 12, fontWeight: 700, background: mod.accentColor, color: 'white', padding: '4px 10px', borderRadius: 20 }}>Done</span>}
                  {!unlocked && <span style={{ fontSize: 12, fontWeight: 700, background: '#e5e7eb', color: '#6b7280', padding: '4px 10px', borderRadius: 20 }}>Unlocks in {days}d</span>}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: mod.tagColor, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {mod.tag}
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 800, color: '#1e1b4b', margin: 0 }}>{mod.title}</h3>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{mod.subtitle}</div>
                </div>
                <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.6, flex: 1 }}>{mod.description}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>🏅 {mod.xpRange}</span>
                  {unlocked && (
                    <span style={{ fontSize: 14, fontWeight: 700, color: mod.accentColor, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {done ? 'Play again' : 'Start'} →
                    </span>
                  )}
                </div>
              </div>
            );

            return unlocked
              ? <Link key={mod.to} to={mod.to} style={{ textDecoration: 'none' }}>{card}</Link>
              : <div key={mod.to}>{card}</div>;
          })}
        </div>

        {/* Badges row */}
        {xp.total > 0 && (
          <div style={{ marginTop: 48 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e1b4b', marginBottom: 16 }}>Your badges</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {xp.completedModules.map(m => (
                <div key={m} style={{
                  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                  🏅 {m.replace(/_/g, ' ')}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No-tracking note */}
        <div style={{
          marginTop: 48,
          background: 'linear-gradient(135deg, #e0e7ff, #ede9fe)',
          borderRadius: 16,
          padding: 24,
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 28 }}>🔒</span>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#3730a3', marginBottom: 4 }}>We practise what we teach</h3>
            <p style={{ fontSize: 14, color: '#4338ca', lineHeight: 1.6, margin: 0 }}>
              We collect only what we need to run the platform: your email address, payment status, and module progress. No advertising trackers, no data brokers, no third-party analytics. Your game progress is stored on your device using localStorage and synced to our servers in Canada (AWS ca-central-1) only when you are signed in. You can request deletion of your data at any time via our <a href="/privacy" style={{ color: '#3730a3', fontWeight: 600 }}>Privacy Notice</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
